import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import grapesjs, { Editor } from 'grapesjs';
import rteExtensions from 'grapesjs-rte-extensions';
import pluginForms from 'grapesjs-plugin-forms';
import 'grapesjs/dist/css/grapes.min.css';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  ArrowLeft,
  Save,
  Loader2,
  Upload,
  AlertTriangle,
  Monitor,
  Tablet,
  Smartphone,
  Layers,
  LayoutGrid,
  Sliders,
  Settings,
  Undo2,
  Redo2,
  Eye,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Link as LinkIcon,
  Highlighter,
  Palette,
  Eraser,
  ChevronUp,
  ChevronDown,
  ArrowUpToLine,
  ArrowDownToLine,
  Copy,
  Trash2,
  Code,
} from 'lucide-react';
import { toast } from 'sonner';

import { storage, cloudFunctions } from '@/lib/firebase';
import { useSite } from '@/hooks/useFirestore';
import { useAuth } from '@/hooks/useAuth';

type SiteWithHtml = {
  id: string;
  name?: string;
  userId?: string;
  generatedHtml?: string;
  previewHtml?: string;
};

type DeviceId = 'desktop' | 'tablet' | 'mobilePortrait';

const FALLBACK_HTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif; margin: 0; color: #1f2937; background: #ffffff; }
  .hero { padding: 96px 24px; text-align: center; background: linear-gradient(135deg,#667eea,#764ba2); color: white; }
  .hero h1 { font-size: 48px; margin: 0 0 16px; }
  .hero p { font-size: 20px; opacity: 0.9; margin: 0 0 24px; }
  .btn { display: inline-block; padding: 12px 24px; background: white; color: #667eea; border-radius: 8px; text-decoration: none; font-weight: 600; }
  .section { padding: 64px 24px; max-width: 1100px; margin: 0 auto; }
</style>
</head>
<body>
<section class="hero">
  <h1>Your headline here</h1>
  <p>Click anywhere to start editing.</p>
  <a href="#" class="btn">Get started</a>
</section>
<section class="section">
  <h2>About</h2>
  <p>Click text to edit. Drag blocks from the left panel to add new sections.</p>
</section>
</body>
</html>`;

function splitHtml(raw: string) {
  const bodyOpenMatch = raw.match(/<body([^>]*)>/i);
  const bodyAttrsRaw = bodyOpenMatch ? bodyOpenMatch[1] : '';
  const bodyClassMatch = bodyAttrsRaw.match(/class=["']([^"']*)["']/i);
  const bodyStyleMatch = bodyAttrsRaw.match(/style=["']([^"']*)["']/i);
  const bodyClass = bodyClassMatch ? bodyClassMatch[1] : '';
  const bodyStyle = bodyStyleMatch ? bodyStyleMatch[1] : '';

  const bodyMatch = raw.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const headMatch = raw.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
  const head = headMatch ? headMatch[1] : '';
  const rawBody = bodyMatch ? bodyMatch[1] : raw;

  const styleBlocks = Array.from(raw.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)).map((m) => m[1]);
  const linkHrefs = Array.from(head.matchAll(/<link[^>]+href=["']([^"']+)["'][^>]*>/gi))
    .filter((m) => /stylesheet/i.test(m[0]) || /fonts\.googleapis/i.test(m[1]))
    .map((m) => m[1]);

  const body = rawBody.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  const headExtras = head
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<link[^>]+rel=["']stylesheet["'][^>]*>/gi, '')
    .trim();

  return { body, css: styleBlocks.join('\n'), linkHrefs, headExtras, bodyClass, bodyStyle };
}

function extractCssVar(css: string, name: string): string | null {
  const re = new RegExp(`--${name}\\s*:\\s*([^;}\\n]+)`, 'i');
  const m = css.match(re);
  return m ? m[1].trim() : null;
}

// Custom block definitions — user-friendly section templates instead of the
// technical "1 Column / 2 Columns / Video / Map" defaults from grapesjs-blocks-basic.
// Each block is a full standalone section so users can stack them like in Canva/Wix.
const CUSTOM_BLOCKS: Array<{
  id: string;
  label: string;
  category: string;
  media?: string;
  content: string;
}> = [
  // ———— Basic elements ————
  {
    id: 'heading',
    label: 'Heading',
    category: 'Basic',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M4 4h3v7h10V4h3v16h-3v-7H7v7H4z"/></svg>',
    content: '<h2 style="font-size:36px;margin:16px 0;">Your heading here</h2>',
  },
  {
    id: 'text',
    label: 'Text',
    category: 'Basic',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M5 5h14v2H5zm0 4h14v2H5zm0 4h10v2H5zm0 4h14v2H5z"/></svg>',
    content:
      '<p style="margin:8px 0;line-height:1.6;">Your text here. Click to edit, or use the toolbar to format.</p>',
  },
  {
    id: 'button',
    label: 'Button',
    category: 'Basic',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><rect x="3" y="8" width="18" height="8" rx="2"/></svg>',
    content:
      '<a href="#" style="display:inline-block;padding:14px 28px;background:#111;color:#fff;text-decoration:none;border-radius:6px;font-weight:600;">Click me</a>',
  },
  {
    id: 'image',
    label: 'Image',
    category: 'Basic',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M21 5v14H3V5h18m-2 2H5v10l4-4 3 3 4-4 3 3V7z"/></svg>',
    content: {
      type: 'image',
      attributes: { alt: 'image' },
      // empty so GrapesJS prompts the asset manager on drop
    } as unknown as string,
  },
  {
    id: 'divider',
    label: 'Divider',
    category: 'Basic',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><rect x="3" y="11" width="18" height="2"/></svg>',
    content: '<hr style="border:none;border-top:1px solid currentColor;opacity:0.2;margin:24px 0;" />',
  },
  {
    id: 'spacer',
    label: 'Spacer',
    category: 'Basic',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M8 7h8v2H8zm0 8h8v2H8z"/></svg>',
    content: '<div style="height:48px;"></div>',
  },
  {
    id: 'video',
    label: 'Video',
    category: 'Basic',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M3 5h18v14H3zm7 3v8l6-4z"/></svg>',
    content: { type: 'video' } as unknown as string,
  },

  // ———— Section templates ————
  {
    id: 'hero',
    label: 'Hero',
    category: 'Sections',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M3 4h18v12H3zm0 14h12v2H3zm14 0h4v2h-4z"/></svg>',
    content: `<section style="padding:120px 24px;text-align:center;">
  <h1 style="font-size:56px;margin:0 0 20px;max-width:900px;margin-left:auto;margin-right:auto;line-height:1.1;">Your big headline goes here</h1>
  <p style="font-size:20px;max-width:640px;margin:0 auto 32px;opacity:0.85;line-height:1.5;">A short supporting sentence that explains what you do and why it matters.</p>
  <a href="#" style="display:inline-block;padding:16px 32px;background:#111;color:#fff;text-decoration:none;border-radius:6px;font-weight:600;">Get started</a>
</section>`,
  },
  {
    id: 'two-column',
    label: 'Text + Image',
    category: 'Sections',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M3 5h8v14H3zm10 0h8v14h-8z"/></svg>',
    content: `<section style="padding:80px 24px;max-width:1200px;margin:0 auto;">
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center;">
    <div>
      <h2 style="font-size:36px;margin:0 0 16px;">Section heading</h2>
      <p style="line-height:1.7;margin:0 0 24px;opacity:0.85;">A paragraph describing what this section is about. Keep it short and focused on a single idea.</p>
      <a href="#" style="display:inline-block;padding:12px 24px;background:#111;color:#fff;text-decoration:none;border-radius:6px;font-weight:600;">Learn more</a>
    </div>
    <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800" style="width:100%;border-radius:12px;" alt="" />
  </div>
</section>`,
  },
  {
    id: 'services-grid',
    label: 'Services',
    category: 'Sections',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M3 5h6v6H3zm8 0h6v6h-6zm8 0h2v6h-2zM3 13h6v6H3zm8 0h6v6h-6zm8 0h2v6h-2z"/></svg>',
    content: `<section style="padding:80px 24px;max-width:1200px;margin:0 auto;">
  <h2 style="text-align:center;font-size:36px;margin:0 0 16px;">What we do</h2>
  <p style="text-align:center;max-width:600px;margin:0 auto 48px;opacity:0.8;">Short intro to your services.</p>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:24px;">
    <div style="padding:32px;border:1px solid currentColor;border-radius:12px;text-align:center;">
      <h3 style="margin:0 0 12px;">Service one</h3>
      <p style="margin:0;opacity:0.85;line-height:1.6;">A one-sentence description of this service.</p>
    </div>
    <div style="padding:32px;border:1px solid currentColor;border-radius:12px;text-align:center;">
      <h3 style="margin:0 0 12px;">Service two</h3>
      <p style="margin:0;opacity:0.85;line-height:1.6;">A one-sentence description of this service.</p>
    </div>
    <div style="padding:32px;border:1px solid currentColor;border-radius:12px;text-align:center;">
      <h3 style="margin:0 0 12px;">Service three</h3>
      <p style="margin:0;opacity:0.85;line-height:1.6;">A one-sentence description of this service.</p>
    </div>
  </div>
</section>`,
  },
  {
    id: 'testimonial',
    label: 'Testimonial',
    category: 'Sections',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M7 7h4v6H7zm0 8h4v2H7zm6-8h4v6h-4zm0 8h4v2h-4z"/></svg>',
    content: `<section style="padding:80px 24px;text-align:center;max-width:800px;margin:0 auto;">
  <blockquote style="font-size:26px;font-style:italic;line-height:1.5;margin:0 0 24px;">"Their work transformed our business in ways I didn't think were possible."</blockquote>
  <p style="margin:0;"><strong>Jane Doe</strong> — Customer</p>
</section>`,
  },
  {
    id: 'cta',
    label: 'Call to Action',
    category: 'Sections',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M3 6h18v12H3zm7 3v6l5-3z"/></svg>',
    content: `<section style="padding:80px 24px;text-align:center;">
  <h2 style="font-size:36px;margin:0 0 16px;">Ready to get started?</h2>
  <p style="max-width:540px;margin:0 auto 28px;opacity:0.85;">A single compelling sentence that gets the click.</p>
  <a href="#" style="display:inline-block;padding:16px 32px;background:#111;color:#fff;text-decoration:none;border-radius:6px;font-weight:600;">Contact us</a>
</section>`,
  },
  {
    id: 'gallery',
    label: 'Gallery',
    category: 'Sections',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M3 5h5v5H3zm6 0h5v5H9zm6 0h5v5h-5zM3 11h5v5H3zm6 0h5v5H9zm6 0h5v5h-5z"/></svg>',
    content: `<section style="padding:80px 24px;max-width:1200px;margin:0 auto;">
  <h2 style="text-align:center;font-size:36px;margin:0 0 40px;">Gallery</h2>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;">
    <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=500" style="width:100%;height:220px;object-fit:cover;border-radius:8px;" />
    <img src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=500" style="width:100%;height:220px;object-fit:cover;border-radius:8px;" />
    <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500" style="width:100%;height:220px;object-fit:cover;border-radius:8px;" />
    <img src="https://images.unsplash.com/photo-1600566753086-00f18fe6ba46?w=500" style="width:100%;height:220px;object-fit:cover;border-radius:8px;" />
    <img src="https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?w=500" style="width:100%;height:220px;object-fit:cover;border-radius:8px;" />
    <img src="https://images.unsplash.com/photo-1598928506311-c55ded91a20c?w=500" style="width:100%;height:220px;object-fit:cover;border-radius:8px;" />
  </div>
</section>`,
  },
  {
    id: 'contact',
    label: 'Contact Info',
    category: 'Sections',
    media:
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7m0 9.5A2.5 2.5 0 1 1 14.5 9A2.5 2.5 0 0 1 12 11.5"/></svg>',
    content: `<section style="padding:80px 24px;text-align:center;max-width:600px;margin:0 auto;">
  <h2 style="font-size:36px;margin:0 0 24px;">Get in touch</h2>
  <p style="margin:8px 0;">123 Main Street, City, State 12345</p>
  <p style="margin:8px 0;"><a href="mailto:hello@example.com" style="color:inherit;">hello@example.com</a></p>
  <p style="margin:8px 0;"><a href="tel:+15551234567" style="color:inherit;">(555) 123-4567</a></p>
</section>`,
  },
];

export default function InlineSiteEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: siteData, isLoading } = useSite(siteId);
  const site = siteData as SiteWithHtml | null | undefined;

  const containerRef = useRef<HTMLDivElement>(null);
  const blocksContainerRef = useRef<HTMLDivElement>(null);
  const layersContainerRef = useRef<HTMLDivElement>(null);
  const styleContainerRef = useRef<HTMLDivElement>(null);
  const traitsContainerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headExtrasRef = useRef<string>('');
  const linkHrefsRef = useRef<string[]>([]);
  const templateCssRef = useRef<string>('');

  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [chromeStyleVars, setChromeStyleVars] = useState<React.CSSProperties>({});
  const [leftTab, setLeftTab] = useState<'blocks' | 'layers'>('blocks');
  const [rightTab, setRightTab] = useState<'design' | 'settings'>('design');
  const [device, setDevice] = useState<DeviceId>('desktop');
  const [hasSelection, setHasSelection] = useState(false);
  const [rteActive, setRteActive] = useState(false);
  // Right-click context menu. Coords are relative to the editor page viewport
  // so we can absolutely-position a React menu regardless of the iframe scroll
  // position — we translate iframe event coords into parent-window coords
  // inside the contextmenu handler.
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const uploadFiles = useCallback(
    async (files: File[]): Promise<string[]> => {
      if (!user || !siteId) throw new Error('Not signed in');
      const urls: string[] = [];
      for (const file of files) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `sites/${user.uid}/${siteId}/${Date.now()}-${safeName}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, file);
        urls.push(await getDownloadURL(r));
      }
      return urls;
    },
    [user, siteId],
  );

  useEffect(() => {
    if (
      !containerRef.current ||
      !blocksContainerRef.current ||
      !layersContainerRef.current ||
      !styleContainerRef.current ||
      !traitsContainerRef.current ||
      !site ||
      editorRef.current
    ) {
      return;
    }

    const initialHtml = site.generatedHtml || site.previewHtml || FALLBACK_HTML;
    const { body, css, linkHrefs, headExtras, bodyClass, bodyStyle } = splitHtml(initialHtml);
    headExtrasRef.current = headExtras;
    linkHrefsRef.current = linkHrefs;
    templateCssRef.current = css;

    const cssBlobUrl = css ? URL.createObjectURL(new Blob([css], { type: 'text/css' })) : null;

    // Template-native names take priority over injected overrides: the
    // `injectThemeOverrides` backend adds a `--primary` even when the
    // template doesn't use it (e.g. luxury-noir uses --gold), which is why
    // the editor chrome was coming up blue on templates that have nothing
    // blue about them.
    const primary =
      extractCssVar(css, 'gold') ||
      extractCssVar(css, 'brand') ||
      extractCssVar(css, 'accent') ||
      extractCssVar(css, 'primary') ||
      '#a78bfa';
    const headingFont =
      extractCssVar(css, 'font-heading') || extractCssVar(css, 'heading-font') || '';
    const bodyFont = extractCssVar(css, 'font-body') || extractCssVar(css, 'body-font') || '';
    setChromeStyleVars({
      ['--gjs-primary-color' as string]: '#0b0d12',
      ['--gjs-secondary-color' as string]: primary,
      ['--gjs-tertiary-color' as string]: '#14171f',
      ['--gjs-quaternary-color' as string]: primary,
      ['--inline-editor-accent' as string]: primary,
      ['--inline-editor-heading-font' as string]: headingFont || 'inherit',
      ['--inline-editor-body-font' as string]: bodyFont || 'inherit',
    });

    const editor = grapesjs.init({
      container: containerRef.current,
      height: '100%',
      width: 'auto',
      fromElement: false,
      storageManager: false,
      showOffsets: true,
      noticeOnUnload: false,
      // Turn OFF the default GrapesJS topbar/panels — we render our own UI
      // around the canvas and mount each manager into a named <div>.
      panels: { defaults: [] },
      plugins: [rteExtensions, pluginForms],
      pluginsOpts: {
        'grapesjs-rte-extensions': {
          base: {
            bold: true,
            italic: true,
            underline: true,
            strikethrough: true,
            link: true,
          },
          format: true,
          fontName: ['Arial', 'Georgia', 'Helvetica', 'Times New Roman', 'Verdana', 'Courier New'],
          fontSize: ['10px', '12px', '14px', '16px', '18px', '24px', '32px', '48px', '64px'],
          fontColor: true,
          hilite: true,
          subscriptSuperscript: true,
          indentOutdent: true,
          list: true,
          align: true,
          actions: ['copy', 'cut', 'paste', 'delete', 'clearFormatting'],
          darkColorPicker: true,
          maxWidth: '800px',
        },
        'grapesjs-plugin-forms': {},
      },
      assetManager: {
        upload: false,
        uploadFile: async (e: Event) => {
          const evt = e as Event & {
            dataTransfer?: DataTransfer;
            target?: HTMLInputElement;
          };
          const fileList = evt.dataTransfer ? evt.dataTransfer.files : evt.target?.files;
          if (!fileList || fileList.length === 0) return;
          const files = Array.from(fileList);
          try {
            const urls = await uploadFiles(files);
            editor.AssetManager.add(urls);
            toast.success(`Uploaded ${urls.length} file${urls.length === 1 ? '' : 's'}`);
          } catch (err) {
            console.error('[inline-editor] upload failed', err);
            toast.error(
              'Upload failed. Enable Firebase Storage on this project or paste an external image URL.',
            );
          }
        },
      },
      blockManager: {
        appendTo: blocksContainerRef.current,
        blocks: CUSTOM_BLOCKS.map((b) => ({
          id: b.id,
          label: b.label,
          category: b.category,
          media: b.media,
          content: b.content,
        })),
      },
      layerManager: {
        appendTo: layersContainerRef.current,
      },
      styleManager: {
        appendTo: styleContainerRef.current,
        sectors: [
          {
            name: 'General',
            open: true,
            buildProps: ['display', 'float', 'position', 'top', 'right', 'left', 'bottom'],
          },
          {
            name: 'Spacing',
            open: false,
            buildProps: ['margin', 'padding'],
          },
          {
            name: 'Size',
            open: false,
            buildProps: ['width', 'min-width', 'max-width', 'height', 'min-height', 'max-height'],
          },
          {
            name: 'Typography',
            open: true,
            buildProps: [
              'font-family',
              'font-size',
              'font-weight',
              'letter-spacing',
              'line-height',
              'color',
              'text-align',
              'text-shadow',
            ],
          },
          {
            name: 'Background',
            open: false,
            buildProps: ['background-color', 'background'],
          },
          {
            name: 'Border & Corners',
            open: false,
            buildProps: ['border-radius', 'border', 'box-shadow'],
          },
          {
            name: 'Effects',
            open: false,
            buildProps: ['opacity', 'transform', 'transition'],
          },
        ],
      },
      traitManager: {
        appendTo: traitsContainerRef.current,
      },
      selectorManager: {
        // We don't expose class management — hide the selector panel entirely.
        componentFirst: true,
        custom: true,
      },
      canvas: {
        styles: cssBlobUrl ? [...linkHrefs, cssBlobUrl] : linkHrefs,
      },
      deviceManager: {
        devices: [
          { id: 'desktop', name: 'Desktop', width: '' },
          { id: 'tablet', name: 'Tablet', width: '768px', widthMedia: '992px' },
          { id: 'mobilePortrait', name: 'Mobile', width: '375px', widthMedia: '575px' },
        ],
      },
    });

    editor.setComponents(body);

    editor.on('load', () => {
      try {
        const canvasDoc = editor.Canvas.getDocument();
        if (!canvasDoc) return;
        const canvasBody = canvasDoc.body;
        if (canvasBody) {
          if (bodyClass) {
            const existing = canvasBody.getAttribute('class') || '';
            canvasBody.setAttribute('class', [existing, bodyClass].filter(Boolean).join(' '));
          }
          if (bodyStyle) {
            const existing = canvasBody.getAttribute('style') || '';
            canvasBody.setAttribute('style', [existing, bodyStyle].filter(Boolean).join(';'));
          }
        }
      } catch (err) {
        console.warn('[inline-editor] canvas body attr reapply failed', err);
      }
    });

    editor.on('update', () => setDirty(true));
    editor.on('component:update', () => setDirty(true));
    editor.on('style:update', () => setDirty(true));
    editor.on('component:selected', () => setHasSelection(true));
    editor.on('component:deselected', () => setHasSelection(false));

    // Track inline text-edit (RTE) sessions so we can show/hide the top
    // formatting toolbar. GrapesJS fires rte:enable/disable whenever the user
    // double-clicks into editable text (or we call enterRte programmatically).
    editor.on('rte:enable', () => setRteActive(true));
    editor.on('rte:disable', () => setRteActive(false));

    // Right-click context menu. We attach on the canvas iframe's document
    // (where the actual clicks happen) and translate coords back into the
    // parent window so the React menu positions correctly.
    editor.on('load', () => {
      const iframe = editor.Canvas.getFrameEl();
      const canvasDoc = editor.Canvas.getDocument();
      if (!iframe || !canvasDoc) return;
      canvasDoc.addEventListener('contextmenu', (ev) => {
        ev.preventDefault();
        const target = ev.target as HTMLElement | null;
        if (target) {
          // Select the clicked component so the context-menu actions apply
          // to it. getComponent() walks up from the DOM node to the GrapesJS
          // component wrapper.
          const comp = (editor.Canvas as unknown as {
            getComponent?: (el: HTMLElement) => unknown;
          }).getComponent?.(target);
          if (comp && typeof (comp as { select?: () => void }).select === 'function') {
            (comp as { select: () => void }).select();
          } else {
            const wrapper = editor.DomComponents.getWrapper();
            const walked = wrapper?.find?.(`[data-gjs-id]`);
            if (walked && walked.length) editor.select(walked[0]);
          }
        }
        const frameRect = iframe.getBoundingClientRect();
        setContextMenu({
          x: frameRect.left + ev.clientX,
          y: frameRect.top + ev.clientY,
        });
      });
      // Dismiss the context menu if the user clicks back into the canvas.
      canvasDoc.addEventListener('click', () => setContextMenu(null));
    });

    editorRef.current = editor;
    setReady(true);

    return () => {
      try {
        editor.destroy();
      } catch {
        // HMR teardown race — ignore.
      }
      editorRef.current = null;
      if (cssBlobUrl) URL.revokeObjectURL(cssBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.id]);

  const handleSave = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor || !siteId) return;
    setSaving(true);
    try {
      const bodyHtml = editor.getHtml();
      const editorCss = editor.getCss();
      const linkTags = linkHrefsRef.current
        .map((href) => `<link rel="stylesheet" href="${href}">`)
        .join('\n');
      const fullHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
${headExtrasRef.current}
${linkTags}
<style data-source="template">${templateCssRef.current}</style>
<style data-source="editor">${editorCss}</style>
</head>
<body>
${bodyHtml}
</body>
</html>`;

      await cloudFunctions.updateSiteContent({
        siteId,
        content: {},
        generatedHtml: fullHtml,
      });
      setDirty(false);
      toast.success('Saved');
    } catch (err) {
      console.error('[inline-editor] save failed', err);
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [siteId]);

  const changeDevice = (next: DeviceId) => {
    setDevice(next);
    editorRef.current?.setDevice(
      next === 'desktop' ? 'Desktop' : next === 'tablet' ? 'Tablet' : 'Mobile',
    );
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFilePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!files.length) return;
    try {
      const urls = await uploadFiles(files);
      editorRef.current?.AssetManager.add(urls);
      editorRef.current?.AssetManager.open();
      toast.success(`Uploaded ${urls.length} file${urls.length === 1 ? '' : 's'}`);
    } catch (err) {
      console.error('[inline-editor] upload failed', err);
      toast.error(
        'Upload failed. Enable Firebase Storage on this project or paste an external image URL.',
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const undo = () => editorRef.current?.UndoManager.undo();
  const redo = () => editorRef.current?.UndoManager.redo();
  const preview = () => editorRef.current?.runCommand('preview');

  // Runs a contentEditable command against the canvas iframe's document.
  // Used by the top text-formatting toolbar — these are the same commands
  // that power Google Docs / Canva's inline text controls.
  const execRte = useCallback((command: string, value?: string) => {
    const ed = editorRef.current;
    if (!ed) return;
    const doc = ed.Canvas.getDocument();
    const win = ed.Canvas.getWindow();
    if (!doc || !win) return;
    try {
      win.focus();
      doc.execCommand(command, false, value);
    } catch (err) {
      console.warn('[inline-editor] execCommand failed', command, err);
    }
  }, []);

  // Wraps the current text selection in a <span> with a custom inline style.
  // Needed for pixel-size font-size / line-height — execCommand('fontSize')
  // only accepts legacy 1-7 sizes which don't map cleanly to px values.
  const wrapSelectionStyle = useCallback(
    (styleProp: string, styleValue: string) => {
      const ed = editorRef.current;
      if (!ed) return;
      const doc = ed.Canvas.getDocument();
      const win = ed.Canvas.getWindow();
      if (!doc || !win) return;
      const sel = win.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (range.collapsed) return;
      const span = doc.createElement('span');
      (span.style as unknown as Record<string, string>)[styleProp] = styleValue;
      try {
        range.surroundContents(span);
      } catch {
        span.appendChild(range.extractContents());
        range.insertNode(span);
      }
      sel.removeAllRanges();
    },
    [],
  );

  const ctxMenuAction = useCallback(
    (action: string) => {
      const ed = editorRef.current;
      if (!ed) {
        setContextMenu(null);
        return;
      }
      const selected = ed.getSelected();
      if (!selected && !['paste'].includes(action)) {
        setContextMenu(null);
        return;
      }
      switch (action) {
        case 'duplicate': {
          const parent = selected!.parent();
          if (parent) {
            const clone = selected!.clone();
            parent.append(clone, { at: (selected!.index?.() ?? 0) + 1 });
            ed.select(clone);
          }
          break;
        }
        case 'delete':
          selected!.remove();
          break;
        case 'move-up': {
          const parent = selected!.parent();
          const idx = selected!.index?.() ?? 0;
          if (parent && idx > 0) {
            parent.components().remove(selected!);
            parent.append(selected!, { at: idx - 1 });
            ed.select(selected!);
          }
          break;
        }
        case 'move-down': {
          const parent = selected!.parent();
          const idx = selected!.index?.() ?? 0;
          if (parent) {
            const total = parent.components().length;
            if (idx < total - 1) {
              parent.components().remove(selected!);
              parent.append(selected!, { at: idx + 1 });
              ed.select(selected!);
            }
          }
          break;
        }
        case 'bring-to-front': {
          const parent = selected!.parent();
          if (parent) {
            parent.components().remove(selected!);
            parent.append(selected!);
            ed.select(selected!);
          }
          break;
        }
        case 'send-to-back': {
          const parent = selected!.parent();
          if (parent) {
            parent.components().remove(selected!);
            parent.append(selected!, { at: 0 });
            ed.select(selected!);
          }
          break;
        }
        case 'copy':
          ed.runCommand('core:copy');
          break;
        case 'paste':
          ed.runCommand('core:paste');
          break;
        case 'edit-html':
          ed.runCommand('core:open-code');
          break;
        default:
          break;
      }
      setContextMenu(null);
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900 text-white">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!site) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900 text-white">
        <div className="flex items-center gap-2 text-red-300">
          <AlertTriangle className="h-5 w-5" />
          <span>Site not found</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="inline-editor-root flex h-screen flex-col bg-[#0b0d12] text-gray-200"
      style={chromeStyleVars}
    >
      <style>{`
        .inline-editor-root .gjs-block {
          width: calc(50% - 8px);
          min-height: auto;
          padding: 10px 8px;
          background: #171922;
          border: 1px solid #22252f;
          color: #d1d5db;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border-radius: 8px;
          transition: all 120ms ease;
          cursor: grab;
        }
        .inline-editor-root .gjs-block:hover {
          border-color: var(--inline-editor-accent, #667eea);
          transform: translateY(-1px);
        }
        .inline-editor-root .gjs-block-label {
          font-size: 11px;
          color: #d1d5db;
        }
        .inline-editor-root .gjs-block-category {
          background: transparent;
          color: #9ca3af;
          border: none;
        }
        .inline-editor-root .gjs-block-categories {
          padding: 8px;
        }
        .inline-editor-root .gjs-blocks-c {
          background: transparent;
          padding: 8px;
          gap: 8px;
        }
        /* Hide the classes / state chooser we don't want users touching */
        .inline-editor-root .gjs-clm-tags,
        .inline-editor-root .gjs-sm-sector__classes,
        .inline-editor-root .gjs-sm-sector-label { }
        /* Style manager polish */
        .inline-editor-root .gjs-sm-sector {
          background: transparent;
          border-bottom: 1px solid #1f2330;
        }
        .inline-editor-root .gjs-sm-sector .gjs-sm-title {
          background: #14171f;
          color: #d1d5db;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 10px 12px;
        }
        .inline-editor-root .gjs-sm-properties {
          background: transparent;
        }
        .inline-editor-root .gjs-field {
          background: #0f1117;
          border-color: #22252f;
          color: #e5e7eb;
        }
        .inline-editor-root .gjs-layer {
          background: transparent;
          color: #d1d5db;
          border-bottom: 1px solid #1f2330;
        }
        .inline-editor-root .gjs-layer-title {
          color: #d1d5db;
        }
        .inline-editor-root .gjs-trt-trait {
          border-bottom: 1px solid #1f2330;
        }
        .inline-editor-root .gjs-trt-trait__label {
          color: #9ca3af;
          font-size: 11px;
        }
        /* Canvas */
        .inline-editor-root .gjs-cv-canvas {
          background: #1a1d24;
          width: 100%;
          height: 100%;
          top: 0;
        }
        .inline-editor-root .gjs-frame-wrapper {
          background: #1a1d24;
        }
      `}</style>

      {linkHrefsRef.current.map((href) => (
        <link key={href} rel="stylesheet" href={href} />
      ))}

      {/* Top bar */}
      <header
        className="flex items-center justify-between border-b border-gray-800 bg-[#0b0d12] px-4 py-2"
        style={{ fontFamily: 'var(--inline-editor-body-font, inherit)' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (dirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
              navigate('/dashboard/sites');
            }}
            className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div
            className="text-sm text-gray-300"
            style={{ fontFamily: 'var(--inline-editor-heading-font, inherit)' }}
          >
            {site.name || 'Untitled'}
          </div>
        </div>

        {/* Device switcher */}
        <div className="flex items-center gap-1 rounded-lg bg-gray-800/60 p-1">
          <button
            onClick={() => changeDevice('desktop')}
            className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs ${device === 'desktop' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}
            title="Desktop"
          >
            <Monitor className="h-3.5 w-3.5" />
            Desktop
          </button>
          <button
            onClick={() => changeDevice('tablet')}
            className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs ${device === 'tablet' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}
            title="Tablet"
          >
            <Tablet className="h-3.5 w-3.5" />
            Tablet
          </button>
          <button
            onClick={() => changeDevice('mobilePortrait')}
            className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs ${device === 'mobilePortrait' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}
            title="Mobile"
          >
            <Smartphone className="h-3.5 w-3.5" />
            Mobile
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={undo}
            disabled={!ready}
            title="Undo"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-40"
          >
            <Undo2 className="h-4 w-4" />
          </button>
          <button
            onClick={redo}
            disabled={!ready}
            title="Redo"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-40"
          >
            <Redo2 className="h-4 w-4" />
          </button>
          <button
            onClick={preview}
            disabled={!ready}
            title="Preview"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-40"
          >
            <Eye className="h-4 w-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilePicked}
            className="hidden"
          />
          <button
            onClick={handleUploadClick}
            disabled={!ready}
            className="flex items-center gap-2 rounded bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            Upload
          </button>
          <a
            href={`/dashboard/sites/${siteId}/edit`}
            className="rounded px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800"
          >
            Structured editor
          </a>
          <button
            onClick={handleSave}
            disabled={saving || !ready}
            className="flex items-center gap-2 rounded px-4 py-1.5 text-sm font-medium text-black hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: 'var(--inline-editor-accent, #2563eb)' }}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </header>

      {/* Text formatting toolbar (Google Docs-style): shown whenever the
          user is inline-editing text. Buttons call contentEditable commands
          on the canvas iframe — same commands Google Docs / Canva use. */}
      {rteActive && (
        <div className="flex flex-wrap items-center gap-1 border-b border-gray-800 bg-[#0e1017] px-3 py-2 text-gray-300">
          <select
            className="h-8 rounded border border-gray-800 bg-[#0b0d12] px-2 text-xs text-gray-200"
            defaultValue="P"
            onChange={(e) => execRte('formatBlock', e.target.value)}
            title="Paragraph style"
          >
            <option value="P">Paragraph</option>
            <option value="H1">Heading 1</option>
            <option value="H2">Heading 2</option>
            <option value="H3">Heading 3</option>
            <option value="H4">Heading 4</option>
            <option value="BLOCKQUOTE">Quote</option>
          </select>

          <select
            className="h-8 w-36 rounded border border-gray-800 bg-[#0b0d12] px-2 text-xs text-gray-200"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) execRte('fontName', e.target.value);
              e.target.value = '';
            }}
            title="Font family"
          >
            <option value="" disabled>
              Font
            </option>
            <option value="inherit">Site default</option>
            <option value="Arial, sans-serif">Arial</option>
            <option value="Georgia, serif">Georgia</option>
            <option value="Helvetica, sans-serif">Helvetica</option>
            <option value="'Times New Roman', serif">Times New Roman</option>
            <option value="Verdana, sans-serif">Verdana</option>
            <option value="'Courier New', monospace">Courier New</option>
            <option value="'Cinzel', serif">Cinzel</option>
            <option value="'Cormorant', serif">Cormorant</option>
            <option value="'Playfair Display', serif">Playfair Display</option>
            <option value="'Inter', sans-serif">Inter</option>
            <option value="'Montserrat', sans-serif">Montserrat</option>
          </select>

          <select
            className="h-8 w-20 rounded border border-gray-800 bg-[#0b0d12] px-2 text-xs text-gray-200"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) wrapSelectionStyle('fontSize', e.target.value);
              e.target.value = '';
            }}
            title="Font size"
          >
            <option value="" disabled>
              Size
            </option>
            {['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '40px', '48px', '64px', '80px'].map(
              (s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ),
            )}
          </select>

          <div className="mx-1 h-6 w-px bg-gray-800" />

          <button
            onClick={() => execRte('bold')}
            title="Bold (⌘B)"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <Bold className="h-4 w-4" />
          </button>
          <button
            onClick={() => execRte('italic')}
            title="Italic (⌘I)"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <Italic className="h-4 w-4" />
          </button>
          <button
            onClick={() => execRte('underline')}
            title="Underline (⌘U)"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <Underline className="h-4 w-4" />
          </button>

          <label
            className="flex h-8 items-center gap-1 rounded px-1 hover:bg-gray-800"
            title="Text color"
          >
            <Palette className="h-4 w-4" />
            <input
              type="color"
              className="h-5 w-5 cursor-pointer border-0 bg-transparent p-0"
              onChange={(e) => execRte('foreColor', e.target.value)}
            />
          </label>
          <label
            className="flex h-8 items-center gap-1 rounded px-1 hover:bg-gray-800"
            title="Highlight"
          >
            <Highlighter className="h-4 w-4" />
            <input
              type="color"
              className="h-5 w-5 cursor-pointer border-0 bg-transparent p-0"
              onChange={(e) => execRte('hiliteColor', e.target.value)}
            />
          </label>

          <div className="mx-1 h-6 w-px bg-gray-800" />

          <button
            onClick={() => execRte('justifyLeft')}
            title="Align left"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <AlignLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => execRte('justifyCenter')}
            title="Align center"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <AlignCenter className="h-4 w-4" />
          </button>
          <button
            onClick={() => execRte('justifyRight')}
            title="Align right"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <AlignRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => execRte('justifyFull')}
            title="Justify"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <AlignJustify className="h-4 w-4" />
          </button>

          <div className="mx-1 h-6 w-px bg-gray-800" />

          <button
            onClick={() => execRte('insertUnorderedList')}
            title="Bulleted list"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => execRte('insertOrderedList')}
            title="Numbered list"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <ListOrdered className="h-4 w-4" />
          </button>

          <button
            onClick={() => {
              const url = window.prompt('Enter URL (leave blank to remove link)');
              if (url === null) return;
              if (url === '') execRte('unlink');
              else execRte('createLink', url);
            }}
            title="Link"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <LinkIcon className="h-4 w-4" />
          </button>

          <button
            onClick={() => execRte('removeFormat')}
            title="Clear formatting"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-800"
          >
            <Eraser className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main: left sidebar / canvas / right sidebar */}
      <div className="flex flex-1 min-h-0">
        {/* Left sidebar */}
        <aside className="flex w-64 flex-col border-r border-gray-800 bg-[#0b0d12]">
          <div className="flex border-b border-gray-800">
            <button
              onClick={() => setLeftTab('blocks')}
              className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium ${leftTab === 'blocks' ? 'border-b-2 text-white' : 'text-gray-500 hover:text-gray-300'}`}
              style={leftTab === 'blocks' ? { borderColor: 'var(--inline-editor-accent)' } : {}}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Blocks
            </button>
            <button
              onClick={() => setLeftTab('layers')}
              className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium ${leftTab === 'layers' ? 'border-b-2 text-white' : 'text-gray-500 hover:text-gray-300'}`}
              style={leftTab === 'layers' ? { borderColor: 'var(--inline-editor-accent)' } : {}}
            >
              <Layers className="h-3.5 w-3.5" />
              Layers
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div
              ref={blocksContainerRef}
              className={leftTab === 'blocks' ? 'block' : 'hidden'}
            />
            <div
              ref={layersContainerRef}
              className={leftTab === 'layers' ? 'block' : 'hidden'}
            />
          </div>
        </aside>

        {/* Canvas */}
        <div className="flex-1 min-w-0 min-h-0" ref={containerRef} />

        {/* Right sidebar */}
        <aside className="flex w-72 flex-col border-l border-gray-800 bg-[#0b0d12]">
          <div className="flex border-b border-gray-800">
            <button
              onClick={() => setRightTab('design')}
              className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium ${rightTab === 'design' ? 'border-b-2 text-white' : 'text-gray-500 hover:text-gray-300'}`}
              style={rightTab === 'design' ? { borderColor: 'var(--inline-editor-accent)' } : {}}
            >
              <Sliders className="h-3.5 w-3.5" />
              Design
            </button>
            <button
              onClick={() => setRightTab('settings')}
              className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium ${rightTab === 'settings' ? 'border-b-2 text-white' : 'text-gray-500 hover:text-gray-300'}`}
              style={rightTab === 'settings' ? { borderColor: 'var(--inline-editor-accent)' } : {}}
            >
              <Settings className="h-3.5 w-3.5" />
              Settings
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {!hasSelection && (
              <div className="p-4 text-xs leading-relaxed text-gray-500">
                Click anything on the canvas to edit its design and settings.
              </div>
            )}
            <div
              ref={styleContainerRef}
              className={rightTab === 'design' && hasSelection ? 'block' : 'hidden'}
            />
            <div
              ref={traitsContainerRef}
              className={rightTab === 'settings' && hasSelection ? 'block' : 'hidden'}
            />
          </div>
        </aside>
      </div>

      {/* Right-click context menu. Fixed-positioned over the whole app
          viewport so it can overlay the iframe canvas — iframes are opaque
          to React event bubbling, so we listen for contextmenu inside the
          canvas doc and translate coords up. */}
      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setContextMenu(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setContextMenu(null);
            }}
          />
          <div
            className="fixed z-50 min-w-[200px] overflow-hidden rounded-lg border border-gray-800 bg-[#14171f] py-1 text-sm text-gray-200 shadow-2xl"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              onClick={() => ctxMenuAction('duplicate')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <Copy className="h-3.5 w-3.5" /> Duplicate
            </button>
            <button
              onClick={() => ctxMenuAction('copy')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
            <button
              onClick={() => ctxMenuAction('paste')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <Copy className="h-3.5 w-3.5 rotate-180" /> Paste
            </button>

            <div className="my-1 border-t border-gray-800" />

            <button
              onClick={() => ctxMenuAction('move-up')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <ChevronUp className="h-3.5 w-3.5" /> Move up
            </button>
            <button
              onClick={() => ctxMenuAction('move-down')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <ChevronDown className="h-3.5 w-3.5" /> Move down
            </button>
            <button
              onClick={() => ctxMenuAction('bring-to-front')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <ArrowUpToLine className="h-3.5 w-3.5" /> Bring to front
            </button>
            <button
              onClick={() => ctxMenuAction('send-to-back')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <ArrowDownToLine className="h-3.5 w-3.5" /> Send to back
            </button>

            <div className="my-1 border-t border-gray-800" />

            <button
              onClick={() => ctxMenuAction('edit-html')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-800"
            >
              <Code className="h-3.5 w-3.5" /> Edit HTML
            </button>

            <div className="my-1 border-t border-gray-800" />

            <button
              onClick={() => ctxMenuAction('delete')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-red-300 hover:bg-red-950/50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}
